import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";

interface NsfwConsentModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConsent: () => Promise<void>;
}

export function NsfwConsentModal({
	open,
	onOpenChange,
	onConsent,
}: NsfwConsentModalProps) {
	const [consented, setConsented] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleConsent = async () => {
		if (!consented) {
			setError("You must confirm that you are 18+ to proceed");
			return;
		}

		setLoading(true);
		setError(null);

		try {
			await onConsent();
			onOpenChange(false);
			setConsented(false);
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: "Failed to submit consent. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		setConsented(false);
		setError(null);
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={handleCancel}>
			<DialogContent className="sm:max-w-lg" data-nova-dialog="true">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<AlertTriangle className="h-5 w-5 text-orange-500" />
						NSFW Content Warning
					</DialogTitle>
					<DialogDescription>
						This model configuration is marked as NSFW (Not Safe For Work) and
						may generate mature content.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<Alert>
						<AlertTriangle className="h-4 w-4" />
						<AlertDescription>
							This content is intended for adults 18 years or older. By
							proceeding, you confirm that you meet the age requirement and
							understand the nature of the content.
						</AlertDescription>
					</Alert>

					<div className="flex items-start space-x-3 p-4 border border-border rounded-lg bg-muted/30">
						<Checkbox
							id="nsfw-consent"
							checked={consented}
							onCheckedChange={(checked) => setConsented(checked === true)}
							className="mt-1"
						/>
						<Label
							htmlFor="nsfw-consent"
							className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
						>
							I confirm that I am 18 years or older and understand this content
							is for mature audiences only.
						</Label>
					</div>

					{error && (
						<Alert variant="destructive">
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={handleCancel} disabled={loading}>
						Cancel
					</Button>
					<Button onClick={handleConsent} disabled={loading || !consented}>
						{loading ? "Processing..." : "I Agree"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
